import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { MessageSquare, Clock, AlertCircle, CheckCircle, Plus } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

interface Ticket {
  id: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  replies: {
    id: string;
    message: string;
    user_email: string;
    created_at: string;
    is_internal: boolean;
  }[];
}

interface TicketListProps {
  onNewTicket: () => void;
}

export function TicketList({ onNewTicket }: TicketListProps) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchTickets();
    }
  }, [user]);

  async function fetchTickets() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          replies:ticket_replies(
            id,
            message,
            user_email,
            created_at,
            is_internal
          )
        `)
        .eq('user_email', user?.email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmitReply = async () => {
    if (!selectedTicket || !reply.trim()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('add_reply', {
        p_ticket_id: selectedTicket.id,
        p_message: reply
      });

      if (error) throw error;
      await fetchTickets();
      setReply('');
    } catch (err) {
      console.error('Error submitting reply:', err);
      setError('Failed to submit reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = {
      open: { color: 'blue', icon: AlertCircle, text: 'Open' },
      in_progress: { color: 'yellow', icon: Clock, text: 'In Progress' },
      waiting_for_response: { color: 'orange', icon: Clock, text: 'Waiting' },
      resolved: { color: 'green', icon: CheckCircle, text: 'Resolved' },
      closed: { color: 'gray', icon: CheckCircle, text: 'Closed' }
    };

    const { color, icon: Icon, text } = config[status as keyof typeof config] || config.open;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>
        <Icon className="w-4 h-4 mr-1" />
        {text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Support Tickets</h2>
        <button
          onClick={onNewTicket}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </button>
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Tickets</h3>
          <p className="mt-1 text-sm text-gray-500">
            Need help? Create a new support ticket.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="col-span-1 bg-white rounded-lg shadow overflow-hidden">
            <div className="divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                    selectedTicket?.id === ticket.id ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    {getStatusBadge(ticket.status)}
                    <span className="text-xs text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">{ticket.subject}</h3>
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    Updated {new Date(ticket.updated_at).toLocaleString()}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Ticket Details */}
          <div className="col-span-2">
            {selectedTicket ? (
              <div className="bg-white rounded-lg shadow h-full flex flex-col">
                <div className="p-6 border-b">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-medium text-gray-900 mb-1">
                        {selectedTicket.subject}
                      </h3>
                      <div className="flex items-center space-x-4">
                        {getStatusBadge(selectedTicket.status)}
                        <span className="text-sm text-gray-500">
                          Created {new Date(selectedTicket.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedTicket.description}
                    </p>
                  </div>

                  {selectedTicket.replies?.filter(r => !r.is_internal).map((reply) => (
                    <div
                      key={reply.id}
                      className={`rounded-lg p-4 ${
                        reply.user_email === selectedTicket.user_email
                          ? 'bg-green-50 border border-green-100'
                          : 'bg-gray-50 border border-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-900">
                          {reply.user_email}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  ))}
                </div>

                {selectedTicket.status !== 'closed' && (
                  <div className="p-6 border-t bg-gray-50">
                    <div className="flex space-x-4">
                      <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Type your reply..."
                        className="flex-1 p-3 border rounded-lg"
                        rows={3}
                      />
                      <button
                        onClick={handleSubmitReply}
                        disabled={!reply.trim() || submitting}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 self-end"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Ticket</h3>
                <p className="text-gray-500">Choose a ticket to view its details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}